import React from 'react';
import { motion } from 'framer-motion';
import { Facebook, Instagram, Linkedin, Mail, MapPin, Phone } from 'lucide-react';
import { BRAND_LOGO, PROPERTIES } from '../utils/constants';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-b from-navy-800 to-navy-900 text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <img 
              src={BRAND_LOGO} 
              alt="Jalsa Group of Hotels" 
              className="h-24 w-auto mb-6 drop-shadow-2xl"
            />
            <p className="text-gray-400 leading-relaxed mb-6 font-light">
              Experience the finest hospitality across three distinct properties 
              in the heart of Bhopal, each offering its unique charm and excellence.
            </p>
            
            {/* Social Links */}
            <div className="flex space-x-4">
              {[
                { icon: Facebook, href: "#" },
                { icon: Instagram, href: "https://www.instagram.com/jalsaretreat/" },
                { icon: Linkedin, href: "https://in.linkedin.com/in/hoteljalsa-retreat-91b922263" }
              ].map((social, index) => (
                <motion.a 
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  className="w-10 h-10 bg-navy-700 hover:bg-gold-400 rounded-full flex items-center justify-center transition-all duration-300 group"
                >
                  <social.icon className="w-5 h-5 text-gray-400 group-hover:text-navy-900 transition-colors duration-300" />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Properties Column */}
          <div>
            <h3 className="text-gold-400 text-lg font-semibold mb-6 tracking-wider">
              OUR PROPERTIES
            </h3>
            <ul className="space-y-4">
              {PROPERTIES.map((property) => (
                <li key={property.id}>
                  <motion.a 
                    href={`#${property.id}`}
                    whileHover={{ x: 5 }}
                    className="text-gray-400 hover:text-gold-400 transition-colors duration-300 font-light block"
                  >
                    <span>{property.name}</span>
                    <p className="text-sm text-gray-500 mt-1">{property.location}</p>
                  </motion.a>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links Column */}
          <div>
            <h3 className="text-gold-400 text-lg font-semibold mb-6 tracking-wider">
              QUICK LINKS
            </h3>
            <ul className="space-y-4">
              {[
                { label: 'About Us', href: '#about' },
                { label: 'Events & Weddings', href: '#events' },
                { label: 'Dining', href: '#dining' },
                { label: 'Gallery', href: '#gallery' },
                { label: 'Careers', href: '#careers' },
                { label: 'Contact Us', href: '#contact' }
              ].map((link) => (
                <li key={link.label}>
                  <motion.a 
                    href={link.href}
                    whileHover={{ x: 5 }}
                    className="text-gray-400 hover:text-gold-400 transition-colors duration-300 font-light block"
                  >
                    {link.label}
                  </motion.a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h3 className="text-gold-400 text-lg font-semibold mb-6 tracking-wider">
              CONTACT US
            </h3>
            <div className="space-y-4">
              {/* Phone */}
              <motion.a 
                href="tel:+917554229796"
                whileHover={{ x: 5 }}
                className="flex items-start space-x-3 text-gray-400 hover:text-gold-400 transition-colors duration-300"
              >
                <Phone className="w-5 h-5 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-light">+91 755 422 9796</p>
                  <p className="text-sm text-gray-500">24/7 Support</p>
                </div>
              </motion.a>

              {/* Email */}
              <motion.a 
                href="mailto:info@jalsahotels.com"
                whileHover={{ x: 5 }}
                className="flex items-start space-x-3 text-gray-400 hover:text-gold-400 transition-colors duration-300"
              >
                <Mail className="w-5 h-5 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-light">info@jalsahotels.com</p>
                  <p className="text-sm text-gray-500">For Inquiries</p>
                </div>
              </motion.a>

              {/* Address */}
              <div className="flex items-start space-x-3 text-gray-400">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-light">Jalsa Group of Hotels</p>
                  <p className="text-sm text-gray-500">Bhopal, Madhya Pradesh</p>
                </div>
              </div>
            </div>

            {/* Newsletter */}
            <div className="mt-8">
              <p className="text-gray-400 mb-3 font-light">Stay Updated</p>
              <div className="flex">
                <input 
                  type="email" 
                  placeholder="Your email"
                  className="flex-1 px-4 py-2 bg-navy-700 border border-navy-600 focus:border-gold-400 focus:outline-none text-white rounded-l"
                />
                <motion.button 
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-6 py-2 bg-gold-400 hover:bg-white text-navy-900 font-semibold rounded-r transition-colors duration-300"
                >
                  Subscribe
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-navy-700">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-500 text-sm font-light">
              © {currentYear} Jalsa Group of Hotels. All rights reserved.
            </p>
            
            <div className="flex space-x-6 text-sm">
              {['Privacy Policy', 'Terms & Conditions', 'Sitemap'].map((link) => (
                <a key={link} href={`#${link.toLowerCase().replace(/\s+/g, '-')}`} className="text-gray-500 hover:text-gold-400 transition-colors duration-300 font-light">
                  {link}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Bottom Border */}
      <div className="h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent" />
    </footer>
  );
};

export default Footer;
